"use client";

import * as React from "react";
import { cn } from "../../utils";
import { agentColorIndex } from "../../data-display/identity-avatar";
import { Label } from "../../primitives/label";
import {
  CHIP_BASE_CSS,
  CHIP_REF_CLASS,
  RichChipContent,
  RichChipEditor,
  chipTypeCss,
  createChipExtension,
  iconMaskUrl,
  useInjectedStyles,
  type ChipNodeMeta,
  type ChipStylesConfig,
  type ChipTypeStyle,
} from "../rich-chip-editor";
import { createMentionSuggestion } from "./suggestion";
import { MENTION_MARKDOWN } from "./markdown";
import {
  MENTION_NODE,
  MENTION_TYPE_ATTR,
  inferMentionableType,
  type MentionableType,
} from "@quovy/types/chip-doc/mention-types";
import type { Mentionable, MentionTextareaProps } from "./types";

export type { Mentionable, MentionTextareaProps } from "./types";
export type { MentionableType, JSONContent, Mark } from "@quovy/types/chip-doc/mention-types";
export {
  MENTION_NODE,
  MENTION_TYPE_ATTR,
  inferMentionableType,
} from "@quovy/types/chip-doc/mention-types";
export {
  MARKDOWN_MENTION_PATH_PREFIX,
  extractMentionIds,
  getPlainText,
  getMarkdownText,
  parseEditorContent,
  parseMentionHref,
  mentionLinkMarkdown,
} from "./text";
export { MENTION_MARKDOWN } from "./markdown";
export { MentionList, type MentionListProps } from "./mention-list";
export { createMentionSuggestion, filterMentionables } from "./suggestion";

// The `@mention` surface of the shared rich chip editor (../rich-chip-editor):
// an `@` popup over people and agents, used by task comments and the agent
// form. It persists `mention` nodes with `{ id, label, mentionType }` attrs —
// the format stored comments have always used, so the chip extension is
// configured with those wire names rather than the family defaults.

/** Wire names this surface persists; see ./types. */
export const MENTION_CHIP_NODE: ChipNodeMeta = {
  name: MENTION_NODE,
  typeAttr: MENTION_TYPE_ATTR,
};

/** Per-type chip color + icon rules (lucide user / bot / wrench). */
const MENTION_TYPE_STYLES: Record<MentionableType, ChipTypeStyle> = {
  user: {
    color: "var(--primary)",
    background: "oklch(from var(--primary) l c h / 0.1)",
    iconSvg:
      "%3Cpath d='M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2'/%3E%3Ccircle cx='12' cy='7' r='4'/%3E",
  },
  agent: {
    color: "var(--primary)",
    background: "oklch(from var(--primary) l c h / 0.1)",
    iconSvg:
      "%3Cpath d='M12 8V4H8'/%3E%3Crect width='16' height='12' x='4' y='8' rx='2'/%3E%3Cpath d='M2 14h2'/%3E%3Cpath d='M20 14h2'/%3E%3Cpath d='M15 13v2'/%3E%3Cpath d='M9 13v2'/%3E",
  },
  tool: {
    color: "var(--primary)",
    background: "oklch(from var(--primary) l c h / 0.1)",
    iconSvg:
      "%3Cpath d='M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z'/%3E",
  },
};

const AGENT_TONES = 10;

// Tile and bot are separate layers since a mask holds one colour; both sit out
// of flow so the label, not the tile, sets the chip's baseline.
const AGENT_CHIP = `.${CHIP_REF_CLASS}[data-ref-tone]:not([data-chip-invalid])`;
const AGENT_TONE_CSS =
  `
      ${AGENT_CHIP} { position: relative; padding-left: calc(0.3rem + 1.45em); }
      ${AGENT_CHIP}::before {
        position: absolute; top: 50%; left: 0.3rem; transform: translateY(-50%);
        width: 1.15em; height: 1.15em; margin: 0; border-radius: 0.3em;
        mask-image: none; -webkit-mask-image: none;
      }
      ${AGENT_CHIP}::after {
        content: ""; position: absolute; top: 50%; left: calc(0.3rem + 0.2em);
        width: 0.75em; height: 0.75em; transform: translateY(-50%);
        background-color: var(--agent-foreground);
        mask: ${iconMaskUrl(MENTION_TYPE_STYLES.agent.iconSvg)} center / contain no-repeat;
        -webkit-mask: ${iconMaskUrl(MENTION_TYPE_STYLES.agent.iconSvg)} center / contain no-repeat;
      }` +
  Array.from({ length: AGENT_TONES }, (_, i) => {
    const tone = `var(--agent-${i + 1})`;
    return `
      .${CHIP_REF_CLASS}[data-ref-tone="${i + 1}"] { color: var(--foreground); background: oklch(from ${tone} l c h / 0.16); }
      .${CHIP_REF_CLASS}[data-ref-tone="${i + 1}"]:not([data-chip-invalid])::before { background-color: ${tone}; }`;
  }).join("");

function mentionToneAttrs({ id, refType }: { id: string; refType: string }) {
  return refType === "agent" ? { "data-ref-tone": String(agentColorIndex(id) + 1) } : {};
}

/** Chip styling for the mention family. */
export const MENTION_CHIP_STYLES: ChipStylesConfig = {
  id: "mention-chip-editor-styles",
  styles: MENTION_TYPE_STYLES,
  extraCss: AGENT_TONE_CSS,
};

/**
 * Mention chip node. `getItems` is read per-keystroke (pass a ref's getter so
 * the editor isn't rebuilt when the mentionable set changes); omit it for
 * read-only rendering, which needs no `@` popup.
 */
export function createMentionChipExtension(getItems?: () => Mentionable[]) {
  return createChipExtension({
    name: MENTION_NODE,
    typeAttr: MENTION_TYPE_ATTR,
    // Comments written before mentions carried a type fall back to the id
    // prefix, exactly as the old renderer did.
    inferType: ({ id }) => inferMentionableType(id),
    labelPrefix: "@",
    markdown: MENTION_MARKDOWN,
    renderAttrs: mentionToneAttrs,
    ...(getItems ? { suggestion: createMentionSuggestion(getItems) } : {}),
  });
}

const MENTION_CHIP_CSS = chipTypeCss(MENTION_TYPE_STYLES) + AGENT_TONE_CSS;

/**
 * A mention rendered outside an editor (e.g. markdown comment bodies), with the
 * exact markup and styles of an editor chip so the two never drift.
 */
export function MentionChip({
  id,
  type,
  className,
  children,
}: {
  id: string;
  type: MentionableType;
  className?: string;
  children: React.ReactNode;
}) {
  useInjectedStyles("rich-chip-editor-base-styles", CHIP_BASE_CSS);
  useInjectedStyles(MENTION_CHIP_STYLES.id, MENTION_CHIP_CSS);
  return (
    <span
      className={cn(CHIP_REF_CLASS, className)}
      data-ref-id={id}
      data-ref-type={type}
      {...mentionToneAttrs({ id, refType: type })}
    >
      {children}
    </span>
  );
}

// ============================================================================
// Editor
// ============================================================================

const EMPTY_MENTION_OPTIONS: Mentionable[] = [];

export function MentionTextarea({
  value,
  onChange,
  placeholder,
  disabled,
  mentionOptions = EMPTY_MENTION_OPTIONS,
  rows = 3,
  label,
  className,
  onSubmit,
  getChipState,
  chipStateKey,
}: MentionTextareaProps) {
  const mentionOptionsRef = React.useRef<Mentionable[]>(mentionOptions);
  mentionOptionsRef.current = mentionOptions;

  const mentionExtension = React.useMemo(
    () => createMentionChipExtension(() => mentionOptionsRef.current),
    [],
  );

  return (
    <div className={cn("space-y-2", className)}>
      {label && <Label className="text-sm font-medium">{label}</Label>}

      <RichChipEditor
        value={value}
        {...(onChange ? { onChange } : {})}
        placeholder={placeholder ?? "Write something…"}
        {...(disabled !== undefined ? { disabled } : {})}
        {...(onSubmit ? { onSubmit } : {})}
        // Submit clears the draft while this field still has focus.
        syncWhileFocused
        rows={rows}
        // Stored comments predate markdown handling; parsing them as markdown
        // would rewrite block attrs on load (e.g. list `tight`).
        markdown={false}
        className="mention-chip-editor"
        chipExtension={mentionExtension}
        chipNode={MENTION_CHIP_NODE}
        chipStyles={MENTION_CHIP_STYLES}
        {...(getChipState ? { getChipState } : {})}
        {...(chipStateKey !== undefined ? { chipStateKey } : {})}
      />
    </div>
  );
}

// ============================================================================
// Read-only renderer
// ============================================================================

/** Read-only renderer for stored mention documents (comments, agent config). */
export function MentionContent({
  value,
  className,
}: {
  value: string | null | undefined;
  className?: string;
}) {
  const mentionExtension = React.useMemo(() => createMentionChipExtension(), []);

  return (
    <RichChipContent
      value={value}
      markdown={false}
      className={cn("mention-chip-content", className)}
      chipExtension={mentionExtension}
      chipNode={MENTION_CHIP_NODE}
      chipStyles={MENTION_CHIP_STYLES}
    />
  );
}
